<template>
  <div class="forum-screen">
    <!-- Header -->
    <div class="forum-header">
      <button class="back-btn" @click="$router.push('/chat/search_chat')">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      <div class="forum-brand">
        <i class="fas fa-comments"></i>
        <h2>Manager & Top‑up Forum</h2>
        <span class="member-badge" v-if="totalMembers > 0">
          <i class="fas fa-users"></i> {{ totalMembers }} members
        </span>
      </div>
      <div class="unread-pill" v-if="unreadCount > 0">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </div>
    </div>

    <!-- Message list -->
    <div class="messages-area" ref="msgContainer" @scroll="onScroll">
      <div v-if="loading && messages.length === 0" class="status-message">Loading messages...</div>
      <div v-else-if="messages.length === 0" class="status-message">
        <i class="fas fa-comment-dots"></i>
        <p>No messages yet. Start the conversation!</p>
      </div>
      <div v-else class="messages-list">
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="msg-row"
          :class="{ 'mine': msg.is_mine, 'theirs': !msg.is_mine }"
        >
          <div class="msg-avatar">
            <img :src="msg.avatar || defaultAvatar" />
            <span class="role-tag" :class="msg.is_manager ? 'manager' : 'topup'">
              {{ msg.is_manager ? 'Mgr' : 'Top' }}
            </span>
          </div>
          <div class="msg-bubble">
            <div class="bubble-top">
              <span class="author">{{ msg.sender_name }}</span>
              <span class="timestamp">{{ formatTime(msg.timestamp) }}</span>
            </div>
            <div class="bubble-content">
              <div v-if="msg.message_type === 'text' || !msg.message_type" v-html="formatMessage(msg.content)"></div>
              <div v-else-if="msg.message_type === 'image'" class="media-attachment">
                <img :src="msg.file_url" @click="previewImage(msg.file_url)" />
                <div v-if="msg.content" class="caption">{{ msg.content }}</div>
              </div>
              <div v-else-if="msg.message_type === 'file'" class="file-attach" @click="downloadFile(msg.file_url)">
                <i :class="getFileIcon(msg.file_name)"></i>
                <span>{{ msg.file_name }}</span>
                <i class="fas fa-download"></i>
              </div>
            </div>
            <div class="bubble-footer" v-if="msg.is_mine">
              <i class="fas" :class="msg.read ? 'fa-check-double read' : 'fa-check'"></i>
            </div>
          </div>
        </div>
      </div>
      <button v-if="showScrollDown" class="scroll-down" @click="scrollToBottom">
        <i class="fas fa-arrow-down"></i>
        <span v-if="newMsgCount">{{ newMsgCount }}</span>
      </button>
    </div>

    <!-- Input area -->
    <div class="input-section">
      <div v-if="selectedFile" class="file-preview-card">
        <div class="preview-details">
          <img v-if="previewType === 'image'" :src="previewUrl" class="preview-img" />
          <div v-else class="preview-icon"><i :class="getFileIcon(selectedFile.name)"></i></div>
          <div class="preview-info">
            <span>{{ selectedFile.name }}</span>
            <span>{{ formatFileSize(selectedFile.size) }}</span>
          </div>
        </div>
        <button class="remove-preview" @click="removeFile"><i class="fas fa-times"></i></button>
      </div>
      <div class="input-bar">
        <button class="attach" @click="$refs.fileInput.click()"><i class="fas fa-paperclip"></i></button>
        <textarea
          v-model="newMessage"
          placeholder="Write your message..."
          rows="1"
          @keydown.enter.exact.prevent="send"
          class="message-field"
        ></textarea>
        <button class="send" @click="send" :disabled="sending || (!newMessage.trim() && !selectedFile)">
          <i v-if="!sending" class="fas fa-paper-plane"></i>
          <i v-else class="fas fa-spinner fa-spin"></i>
        </button>
      </div>
      <input type="file" ref="fileInput" style="display:none" @change="onFilePick" />
    </div>

    <!-- Lightbox -->
    <div v-if="lightbox" class="lightbox" @click.self="lightbox = null">
      <img :src="lightbox" />
      <button @click="lightbox = null"><i class="fas fa-times"></i></button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ForumGlobal',
  data() {
    return {
      messages: [],
      newMessage: '',
      loading: false,
      sending: false,
      selectedFile: null,
      previewUrl: null,
      previewType: null,
      unreadCount: 0,
      totalMembers: 0,
      showScrollDown: false,
      newMsgCount: 0,
      isAtBottom: true,
      lightbox: null,
      pollInterval: null,
      defaultAvatar: require('@/assets/images/avatars/1.png'),

    };
  },
  mounted() {
    this.loadMessages(true);
    this.fetchUnread();
    this.pollInterval = setInterval(() => {
      this.fetchUnread();
      if (this.isAtBottom) this.loadMessages(false);
    }, 8000);
  },
  beforeDestroy() {
    if (this.pollInterval) clearInterval(this.pollInterval);
  },
  methods: {
    async loadMessages(markRead = true) {
      this.loading = true;
      try {
        const mark = markRead ? 'true' : 'false';
        const res = await axios.get(`/api/v1/manager/forum/messages/?mark_read=${mark}`);
        this.messages = res.data.messages;
        this.totalMembers = res.data.total_members;
        if (markRead) this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      } catch (err) {
        console.error('Load error', err);
      } finally {
        this.loading = false;
      }
    },
    async send() {
      if ((!this.newMessage.trim() && !this.selectedFile) || this.sending) return;
      this.sending = true;
      const fd = new FormData();
      fd.append('message', this.newMessage.trim());
      fd.append('message_type', this.selectedFile ? this.getFileType(this.selectedFile) : 'text');
      if (this.selectedFile) fd.append('file', this.selectedFile);
      try {
        await axios.post('/api/v1/manager/forum/send/', fd);
        this.newMessage = '';
        this.removeFile();
        await this.loadMessages(true);
        this.fetchUnread();
      } catch (err) {
        console.error('Send error', err);
        this.$store.commit('setSnackBarMessage', 'Message not sent');
        this.$store.commit('setSnackBarColor', 'red');
        this.$store.commit('activateSnackBar', true);
      } finally {
        this.sending = false;
      }
    },
    async fetchUnread() {
      try {
        const res = await axios.get('/api/v1/manager/forum/unread/');
        this.unreadCount = res.data.unread;
      } catch (err) { console.error('Unread error', err); }
    },
    onFilePick(e) {
      const file = e.target.files[0];
      if (!file) return;
      const max = file.type.startsWith('video/') ? 2000*1024*1024 : 10*1024*1024;
      if (file.size > max) {
        alert(`Max ${max/(1024*1024)} MB`);
        return;
      }
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.previewUrl = ev.target.result;
        this.previewType = file.type.startsWith('image/') ? 'image' : 'file';
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    },
    removeFile() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.previewType = null;
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image';
      if (file.type.startsWith('video/')) return 'video';
      return 'file';
    },
    getFileIcon(name) {
      if (!name) return 'fas fa-file';
      const ext = name.split('.').pop().toLowerCase();
      const map = {
        pdf: 'fas fa-file-pdf', doc: 'fas fa-file-word', docx: 'fas fa-file-word',
        xls: 'fas fa-file-excel', xlsx: 'fas fa-file-excel',
        jpg: 'fas fa-file-image', png: 'fas fa-file-image',
        mp4: 'fas fa-file-video'
      };
      return map[ext] || 'fas fa-file';
    },
    formatFileSize(b) {
      if (!b) return '0 B';
      const s = ['B','KB','MB','GB'];
      const i = Math.floor(Math.log(b)/Math.log(1024));
      return (b/Math.pow(1024,i)).toFixed(1)+' '+s[i];
    },
    formatMessage(t) {
      if (!t) return '';
      return t.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>').replace(/\n/g,'<br>');
    },
    formatTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      const now = new Date();
      const diff = Math.floor((now-d)/60000);
      if (diff < 1) return 'Just now';
      if (diff < 60) return `${diff}m`;
      if (diff < 1440) return `${Math.floor(diff/60)}h`;
      return `${Math.floor(diff/1440)}d`;
    },
    scrollToBottom() {
      const c = this.$refs.msgContainer;
      if (c) {
        c.scrollTop = c.scrollHeight;
        this.isAtBottom = true;
        this.showScrollDown = false;
        this.newMsgCount = 0;
      }
    },
    onScroll() {
      const c = this.$refs.msgContainer;
      if (!c) return;
      const atBottom = c.scrollTop + c.clientHeight >= c.scrollHeight - 100;
      this.isAtBottom = atBottom;
      this.showScrollDown = !atBottom;
      if (atBottom) this.newMsgCount = 0;
    },
    previewImage(url) { this.lightbox = url; },
    downloadFile(url) { window.open(url, '_blank'); },
  }
};
</script>

<style scoped>
.forum-screen {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f7f9fc;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}

.forum-header {
  background: white;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e9edf2;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  flex-shrink: 0;
}
.back-btn {
  background: #f0f2f5;
  border: none;
  border-radius: 40px;
  padding: 8px 18px;
  font-weight: 500;
  cursor: pointer;
  color: #2c3e50;
  transition: 0.2s;
}
.back-btn:hover {
  background: #e2e6ea;
}
.forum-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.forum-brand i {
  font-size: 26px;
  color: #3b82f6;
}
.forum-brand h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
}
.member-badge {
  background: #eef2ff;
  padding: 4px 12px;
  border-radius: 40px;
  font-size: 13px;
  color: #1e40af;
}
.unread-pill {
  background: #ef4444;
  color: white;
  padding: 4px 14px;
  border-radius: 40px;
  font-size: 13px;
  font-weight: bold;
}

.messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  background: #ffffff;
}
.messages-list {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.msg-row {
  display: flex;
  gap: 14px;
  animation: fadeSlide 0.2s ease;
}
.msg-row.mine {
  flex-direction: row-reverse;
}
.msg-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 48px;
}
.msg-avatar img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid white;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.role-tag {
  font-size: 9px;
  font-weight: bold;
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 30px;
  margin-top: 4px;
}
.role-tag.manager {
  background: #3b82f6;
  color: white;
}
.role-tag.topup {
  background: #10b981;
  color: white;
}
.msg-bubble {
  max-width: 70%;
  background: #f1f5f9;
  border-radius: 24px;
  padding: 10px 16px;
  box-shadow: 0 1px 0 rgba(0,0,0,0.05);
}
.mine .msg-bubble {
  background: #dbeafe;
  border-bottom-right-radius: 6px;
}
.theirs .msg-bubble {
  border-bottom-left-radius: 6px;
}
.bubble-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 12px;
  margin-bottom: 6px;
}
.author {
  font-weight: 700;
  color: #1f2937;
}
.timestamp {
  color: #64748b;
  font-size: 10px;
}
.bubble-content {
  font-size: 14px;
  line-height: 1.45;
  color: #0f172a;
  word-break: break-word;
}
.media-attachment img {
  max-width: 220px;
  border-radius: 20px;
  cursor: pointer;
  margin-top: 4px;
}
.caption {
  font-size: 12px;
  margin-top: 6px;
  color: #475569;
}
.file-attach {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 8px 12px;
  border-radius: 50px;
  cursor: pointer;
  border: 1px solid #e2e8f0;
}
.file-attach i:first-child {
  font-size: 20px;
  color: #3b82f6;
}
.file-attach span {
  flex: 1;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bubble-footer {
  text-align: right;
  margin-top: 4px;
  font-size: 11px;
  color: #475569;
}
.read {
  color: #3b82f6;
}
.scroll-down {
  position: fixed;
  bottom: 110px;
  right: 30px;
  background: white;
  border: none;
  border-radius: 30px;
  padding: 8px 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #3b82f6;
  z-index: 10;
}
.scroll-down span {
  background: #ef4444;
  color: white;
  border-radius: 20px;
  padding: 0px 8px;
  font-size: 12px;
}
.status-message {
  text-align: center;
  padding: 50px 20px;
  color: #6c757d;
}
.status-message i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}
.input-section {
  background: white;
  border-top: 1px solid #e9edf2;
  padding: 12px 20px;
  flex-shrink: 0;
}
.file-preview-card {
  background: #f8fafc;
  border-radius: 40px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.preview-details {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}
.preview-img {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  object-fit: cover;
}
.preview-icon {
  width: 42px;
  height: 42px;
  background: #eef2ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-info {
  display: flex;
  flex-direction: column;
  font-size: 12px;
}
.remove-preview {
  background: none;
  border: none;
  cursor: pointer;
  color: #5b6e8c;
}
.input-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  border-radius: 60px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
}
.attach, .send {
  background: none;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  transition: 0.2s;
}
.send {
  background: #3b82f6;
  color: white;
}
.send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.message-field {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  padding: 10px 0;
  max-height: 100px;
}
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.lightbox img {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 16px;
}
.lightbox button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0,0,0,0.5);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
  font-size: 18px;
}
@keyframes fadeSlide {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (max-width: 640px) {
  .forum-header {
    padding: 10px 16px;
    flex-wrap: wrap;
  }
  .msg-bubble {
    max-width: 85%;
  }
  .msg-avatar {
    width: 40px;
  }
  .msg-avatar img {
    width: 36px;
    height: 36px;
  }
  .media-attachment img {
    max-width: 180px;
  }
}
</style>
